import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Field, GoldButton, GhostButton } from "../../components/admin/AdminUI";
import ImageUpload from "../../components/admin/ImageUpload";

const EMPTY = {
  name: "", brand: "", price: "", promo_price: "", volume_ml: "", gender: "unissex", description: "", family: "",
  notes_top: "", notes_heart: "", notes_base: "", stock: 0, images: [], featured: false, is_launch: false, best_seller: false, active: true,
};

const FLAGS = [["active", "Ativo no site"], ["featured", "Destaque na página inicial"], ["is_launch", "Lançamento"], ["best_seller", "Mais vendido"]];

export default function ProductForm({ open, onOpenChange, product, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF(product ? { ...EMPTY, ...product, promo_price: product.promo_price ?? "" } : EMPTY);
  }, [open, product]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    const price = parseFloat(String(f.price).replace(",", "."));
    const promo = f.promo_price === "" ? null : parseFloat(String(f.promo_price).replace(",", "."));
    if (!f.name.trim() || !f.brand.trim()) return toast.error("Informe nome e marca.");
    if (!(price > 0)) return toast.error("Informe um preço de venda válido.");
    if (promo !== null && !(promo > 0 && promo < price)) return toast.error("O preço promocional deve ser menor que o preço de venda.");
    if (!(parseInt(f.volume_ml, 10) > 0)) return toast.error("Informe o volume em ml.");
    const body = { ...f, price, promo_price: promo, volume_ml: parseInt(f.volume_ml, 10), stock: parseInt(f.stock, 10) || 0 };
    ["id", "slug", "request_count", "created_at", "updated_at"].forEach((k) => delete body[k]);
    setSaving(true);
    try {
      if (product) await api.put(`/admin/products/${product.id}`, body);
      else await api.post("/admin/products", body);
      toast.success(product ? "Produto atualizado" : "Produto cadastrado");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto border-[#D4AF37]/20 bg-[#141417]" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-medium">{product ? "Editar produto" : "Novo produto"}</DialogTitle>
          <DialogDescription>A primeira foto é a principal. Imagens são otimizadas automaticamente.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
          <Field label="Fotos" className="sm:col-span-2"><ImageUpload multiple value={f.images} onChange={set("images")} testid="product-images" aspect="aspect-[4/5]" /></Field>
          <Field label="Nome do perfume *"><Input value={f.name} onChange={set("name")} data-testid="product-form-name" /></Field>
          <Field label="Marca *"><Input value={f.brand} onChange={set("brand")} data-testid="product-form-brand" /></Field>
          <Field label="Preço de venda (R$) *"><Input inputMode="decimal" value={f.price} onChange={set("price")} data-testid="product-form-price" /></Field>
          <Field label="Preço promocional (R$)" hint="Deixe vazio se não houver"><Input inputMode="decimal" value={f.promo_price} onChange={set("promo_price")} data-testid="product-form-promo-price" /></Field>
          <Field label="Volume (ml) *"><Input inputMode="numeric" value={f.volume_ml} onChange={set("volume_ml")} data-testid="product-form-volume" /></Field>
          <Field label="Gênero *">
            <Select value={f.gender} onValueChange={set("gender")}>
              <SelectTrigger data-testid="product-form-gender"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="masculino">Masculino</SelectItem><SelectItem value="feminino">Feminino</SelectItem><SelectItem value="unissex">Unissex</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Família olfativa"><Input value={f.family} onChange={set("family")} placeholder="Ex.: Oriental Amadeirado" data-testid="product-form-family" /></Field>
          <Field label="Estoque (unidades)"><Input inputMode="numeric" value={f.stock} onChange={set("stock")} data-testid="product-form-stock" /></Field>
          <Field label="Notas de saída"><Input value={f.notes_top} onChange={set("notes_top")} data-testid="product-form-notes-top" /></Field>
          <Field label="Notas de coração"><Input value={f.notes_heart} onChange={set("notes_heart")} data-testid="product-form-notes-heart" /></Field>
          <Field label="Notas de fundo" className="sm:col-span-2"><Input value={f.notes_base} onChange={set("notes_base")} data-testid="product-form-notes-base" /></Field>
          <Field label="Descrição detalhada" className="sm:col-span-2"><Textarea rows={5} value={f.description} onChange={set("description")} data-testid="product-form-description" /></Field>
          <div className="grid gap-3 sm:col-span-2 sm:grid-cols-2">
            {FLAGS.map(([k, l]) => (
              <label key={k} className="flex items-center gap-3 rounded-xl border border-white/5 px-4 py-3 text-sm">
                <Switch checked={!!f[k]} onCheckedChange={set(k)} data-testid={`product-form-${k}`} />{l}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3 sm:col-span-2">
            <GhostButton type="button" onClick={() => onOpenChange(false)}>Cancelar</GhostButton>
            <GoldButton type="submit" disabled={saving} data-testid="product-form-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Salvar</GoldButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
